import React, { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import { useScreenRecognition } from 'utils/hooks';
import { isBrowser } from 'utils/browser';
import ScreenRecognitionContext from 'utils/context/screenRecognitionContext';
import Breadcrumbs from 'common/Breadcrumbs';
import { LayoutProps, StaticPropsType } from './models';
import Header from '../../components/Header';
import VideoHeader from '../../components/VideoHeader';
import Footer from '../../components/Footer';

const Layout: FC<LayoutProps> = ({
  children,
  headerTransparent,
  className,
  langProps,
  breadcrumbs,
  isVideoHeader,
  isWithoutFooter,
}) => {
  const CSSclassNames = classNames('layout', className, {
    'layout--transparent': headerTransparent,
  });

  const screenResolution = useScreenRecognition();
  const screenResolutionProviderValues = useMemo(() => ({ screenResolution }), [screenResolution]);

  const {
    allBrandSettings,
    socialMedia,
    allUmbracoSearchResults: searchResultsPage,
    header: { isHeaderTopVisible },
  }: StaticPropsType = useStaticQuery(graphql`
    {
      allUmbracoSearchResults {
        nodes {
          url
          langProps {
            lang
          }
        }
      }
      allBrandSettings {
        nodes {
          brandName
          url
          lang
          brandLogo {
            ...umbracoImage
          }
          customLogoRegex
          customBrandLogo {
            ...umbracoImage
          }
          videoTemplateBrandLogo {
            ...umbracoImage
          }
          videoTemplateBrandLogoBackground {
            ...umbracoImage
          }
        }
      }
      header {
        isHeaderTopVisible
      }
      socialMedia {
        socialMediaItems {
          properties {
            socialMediaIcon
            socialMediaAriaLabel
            isVisible
            socialMediaIconColor {
              label
              value
            }
            socialMediaHoverIconColor {
              label
              value
            }
            socialMediaLink {
              name
              target
              url
              icon
              published
            }
          }
        }
      }
    }
  `);

  const brandSettings = allBrandSettings.nodes.filter(({ lang }) => lang === langProps.lang)[0];
  const videoBrandLogo = brandSettings.videoTemplateBrandLogo;
  const videoBrandLogoBackground = brandSettings.videoTemplateBrandLogoBackground;
  const [isCustomLogo, setCustomLogo] = useState(false);

  useEffect(() => {
    if (brandSettings?.customLogoRegex && isBrowser() && window?.location?.pathname) {
      const customRegex = new RegExp(brandSettings?.customLogoRegex);
      const isRegexp = customRegex.test(window.location.pathname);
      if (isRegexp) {
        setCustomLogo(true);
        const layout = document.querySelector('.layout');
        layout?.classList.add('custom-variant');
      }
    }
  }, [brandSettings]);

  return (
    <ScreenRecognitionContext.Provider value={screenResolutionProviderValues.screenResolution}>
      <div className={CSSclassNames}>
        {!isVideoHeader ? (
          <Header
            isCustomLogo={isCustomLogo}
            brandSettings={brandSettings}
            socialMedia={socialMedia}
            searchResultsPage={searchResultsPage}
            langProps={langProps}
            isHeaderTopVisible={isHeaderTopVisible}
          />
        ) : (
          <VideoHeader
            brandLogo={videoBrandLogo}
            brandLogoBackground={videoBrandLogoBackground}
            brandName={brandSettings.brandName}
            homepageLink={brandSettings.url}
          />
        )}
        <main id="main" className="layout__main">
          {breadcrumbs?.breadCrumb?.length ? <Breadcrumbs breadcrumbs={breadcrumbs} /> : null}
          {children}
        </main>
        {!isWithoutFooter ? (
          <Footer
            isCustomLogo={isCustomLogo}
            brandSettings={brandSettings}
            socialMedia={socialMedia}
            langProps={langProps}
          />
        ) : null}
      </div>
    </ScreenRecognitionContext.Provider>
  );
};

export default Layout;
